import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Input, message, notification, Row, Space, Table, Upload } from "antd";
import { DeleteOutlined, EyeOutlined, UploadOutlined } from "@ant-design/icons";
import { GoDotFill } from "react-icons/go";
import { useNavigate } from "react-router-dom";

import { containerService, dropdownService, loadingPlanService } from "../../../services";

import MaterialLikeInput from "../../../components/MaterialLikeInput";
import MaterialLikeSelect from "../../../components/MaterialLikeSelect";
import MaterialLikeDatePicker from "../../../components/MaterialLikeDatePicker";
import MaterialLikeTextArea from "../../../components/MaterialLikeTextArea ";
import VehicleDropDown from "../../adminPortal/loadingplan/VehicleDropdown";
import { LuTriangleAlert } from "react-icons/lu";

function CreateLoadPlans() {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [vehicleLoader, setVehicleLoader] = useState(false);

  const [vehiclesBk, setVehiclesBk] = useState([]);
  const [carLoading, setCarLoading] = useState(false);

  const [shippingLine, setShippingLine] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [containerSize, setContainerSize] = useState([]);
  const [newCarOptions, setNewCarOptions] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [container_number, setContainer_number] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);

  // API call function
  const validate = (value) => (value === null || value === "" ? "-" : value);

  const fetchCarOptions = (query, setOptions) => {
    // If query is empty, show all vehicles
    if (!query) {
      setOptions(vehiclesBk);
      return;
    }

    setVehicleLoader(true);

    try {
      // Always filter from the original backup state
      const filtered = vehiclesBk.filter((car) => {
        const vehicleMatch = car.data.vehicle?.toLowerCase().includes(query.toLowerCase());
        const chasisMatch = car.data.chasis_no?.toLowerCase().includes(query.toLowerCase());
        // const containerMatch = car.data.container_no?.toLowerCase().includes(query.toLowerCase());
        // const customerMatch = car.data.customer_name?.toLowerCase().includes(query.toLowerCase());

        // return vehicleMatch || chasisMatch || containerMatch || customerMatch;
        return vehicleMatch || chasisMatch;
      });

      setOptions(filtered);
    } catch (error) {
      console.error("Error filtering vehicle options:", error);
      setOptions([]);
    } finally {
      setVehicleLoader(false);
    }
  };

  // Delete handler
  const handleDelete = (id) => {
    setTableData((prevData) => prevData.filter((item) => item.id !== id));
  };

  // Handle selection from dropdown
  const handleSelect = (_, option) => {
    const { data } = option;

    const selectedCar = {
      id: data.id,
      name: data.vehicle,
      chasis_no: data.chasis_no,
      year: data.year,
      product: data.product,
      lot_number: data.lot_number,
      vehicle_type: data.vehicle_type,
      title: data.title,
      remarks: ""
    };

    setTableData((prev) => {
      // Check if vehicle already exists
      if (prev.some((car) => car.chasis_no === selectedCar.chasis_no)) {
        message.error(`The chasis number ${selectedCar.chasis_no} already exists.`);
        return prev;
      }

      // Check if limit exceeded
      if (prev.length >= 5) {
        message.error("You can only add up to 5 vehicles.");
        return prev;
      }

      return [...prev, selectedCar];
    });

    form.setFieldsValue({ new_car: "" });
  };

  const handleRemarkChange = (value, record) => {
    setTableData((prev) =>
      prev.map((item) =>
        item.id === record.id ? { ...item, remarks: value } : item
      )
    );
  };

  const columns = [
    { title: "Vehicle", dataIndex: "name", key: "name" },
    { title: "Year", dataIndex: "year", key: "year" },
    { title: "Model", dataIndex: "product", key: "product" },
    { title: "Vehicle Type", dataIndex: "vehicle_type", key: "vehicle_type" },
    { title: "Lot No", dataIndex: "lot_number", key: "lot_number" },
    { title: "Chassis Number", dataIndex: "chasis_no", key: "chasis_no" },
    {
      title: "Title", dataIndex: "title", key: "title", render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <p style={{ margin: 0 }}>{text}</p>

          {record.title === "No" && (
            <LuTriangleAlert color="red" size={16} />
          )}
        </div>
      )
    },
    {
      title: "Remarks",
      key: "remarks",
      render: (_, record) =>
        record.title?.toLowerCase() === "no" ? (
          <Input
            required
            placeholder="Enter remarks"
            value={record.remarks}
            onChange={(e) => handleRemarkChange(e.target.value, record)}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Action",
      key: "Action",
      render: (_, record) => (
        <Space size="middle">
          <EyeOutlined
            className="hover:text-primary cursor-pointer"
            onClick={() => window.open(`/vehicle/${record.id}`, "_blank", "noopener,noreferrer")}
          />
          <DeleteOutlined onClick={() => handleDelete(record.id)} />
        </Space >
      ),
    },
  ];

  const getShippingLine = async () => {
    const response = await dropdownService.fetchShippingLines();
    if (response.success) setShippingLine(response.data);
  };

  const getDestinations = async () => {
    const { success, data } = await dropdownService.fetchDestination();
    if (success) {
      const uniqueDestinations = Array.from(
        new Map(data.map((item) => [item.name, item])).values()
      );
      setDestinations(uniqueDestinations);
    }
  };

  const getContainerSize = async () => {
    const { success, data } = await dropdownService.fetchContainerSizes();
    if (success) setContainerSize(data);

    const { success: resSuccess, data: resData } = await containerService.fetchlatestContainerId();
    if (resSuccess) setContainer_number(resData.id);
  };

  const loadCustomerYardVehicles = async () => {

    try {
      setCarLoading(true);
      const response = await dropdownService.fetchVehiclesOnSearch();
      setCarLoading(false);
      if (response.success) {
        const data = response.data.map((car) => ({
          vehicle: car?.vehicle,
          value: `${validate(car?.vehicle)} || ${validate(car?.chasis_no)} || ${validate(car?.year)} || ${validate(car?.title)} || ${validate(car?.customer_name)}`,
          label: `${validate(car?.vehicle)} || ${validate(car?.chasis_no)} || ${validate(car?.year)} || ${validate(car?.title)} || ${validate(car?.customer_name)}`,
          data: car,
        }));

        setNewCarOptions(data);
        setVehiclesBk(data);
      }
    } catch (error) {
      console.error("Error fetching vehicle options:", error);
      setNewCarOptions([]);
    }
  }

  // const onFinish = async (values, val) => {
  //   setLoading(true);
  //   // console.log(val);
  //   // const data2 = form2.getFieldsValue();
  //   // if (Object.keys(data).length > 0) {
  //   // const data = form.getFieldsValue();
  //   const additionalData =
  //     val === "Send to Ctrade"
  //       ? { load: "Send to Ctrade" }
  //       : val === "save"
  //         ? { save: "Save" }
  //         : {};
  //   // const merged = { ...data1, ...data2 };
  //   const carid = tableData.map((car) => car.id);
  //   const { new_car, ...newValues } = values;
  //   const payload = {
  //     ...newValues,
  //     etd_date: values.etd_date.format("YYYY-MM-DD"),
  //     erd_date: values.erd_date.format("YYYY-MM-DD"),
  //     auto_cut_off_date: values.auto_cut_off_date.format("YYYY-MM-DD"),
  //     cargo_cut_off_date: values.cargo_cut_off_date.format("YYYY-MM-DD"),
  //     title_cut_off_date: values.title_cut_off_date.format("YYYY-MM-DD"),
  //     doc_cut_off_date: values.doc_cut_off_date.format("YYYY-MM-DD"),
  //     eta_date: values.eta_date.format("YYYY-MM-DD"),
  //     carid,
  //     ...additionalData,
  //   };
  //   // console.log(payload);
  //   // }
  //   const response = await loadingPlanService.addClientPlan(payload);
  //   if (response.success) {
  //     notification.success({
  //       message: "Successfully added!",
  //       description: response.message,
  //       placement: "topRight",
  //     });

  //     //Redirect
  //     setTimeout(() => {
  //       navigate("/customers-portal/load-plan/send-load-plan");
  //     }, 1500);

  //   }
  //   setLoading(false);
  // };


  const onFinish = async (values, val) => {
    setLoading(true);

    const additionalData = val === "Send to Ctrade" ? { load: "Send to Ctrade" } : (val === "save" ? { save: "Save" } : {});

    const { new_car, ...newValues } = values;

    // Use FormData to send file + other data
    const formData = new FormData();

    tableData.forEach((car) => {
      formData.append("carid[]", car.id);          // backend expects car IDs
      formData.append("remarks[" + car.id + "]", car.remarks || ""); // send remarks
    });

    Object.entries(newValues).forEach(([key, value]) => {
      if (value?.format) {
        // moment date
        formData.append(key, value.format("YYYY-MM-DD"));
      } else {
        formData.append(key, value);
      }
    });

    // Add file if exists
    if (uploadedFile) {
      formData.append("file", uploadedFile); // backend expects 'file'
    }

    // Add additionalData
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      const response = await loadingPlanService.addClientPlan(formData, { headers: { "Content-Type": "multipart/form-data" } });

      if (response.success) {
        notification.success({ message: "Successfully added!", description: response.message, placement: "topRight" });

        // Redirect after short delay
        setTimeout(() => {
          navigate("/customers-portal/load-plan/send-load-plan");
        }, 1500);
      }
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };

  const handleSubmit = (val) => {
    const missingRemarks = tableData.filter(
      (car) =>
        car.title?.toLowerCase() === "no" &&
        (!car.remarks || car.remarks.trim() === "")
    );
    if (missingRemarks.length > 0) {
      message.error("Remarks are mandatory for vehicles without title.");
      return;
    }

    form.validateFields().then((values) => {
      onFinish(values, val);
    }).catch((errorInfo) => {
      console.error("Validation failed:", errorInfo);
    });
  };

  const filteredOptions = useMemo(() => {
    const selected = tableData.map((car) => car.chasis_no);
    return newCarOptions.filter(
      (opt) => !selected.includes(opt.data.chasis_no)
    );
  }, [newCarOptions, tableData]);
  useEffect(() => {
    document.title = "Ctrade - Load Plans";
    getShippingLine();
    getContainerSize();
    getDestinations();
    loadCustomerYardVehicles();
  }, []);

  return (
    <>
      <h1 className='mt-5 mb-6 text-sm font-normal uppercase flex items-center gap-2'>
        <span><GoDotFill style={{ color: "#B83D3D" }} /></span>ADD PLANS
      </h1>

      <div style={{ padding: "15px", border: "1px solid #ccc", borderRadius: "8px", backgroundColor: "#FFFFFF" }}>
        <div style={{ padding: "15px" }}>
          <Form requiredMark={false} form={form} layout="vertical" onFinish={(values) => onFinish(values, null)} initialValues={{ layout: "vertical" }}>
            <Row gutter={24}>

              <Col span={24} className="mb-1" >
                <h1 className="text-[#6A7282] uppercase text-sm">Booking details</h1>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="booking_number"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}

                >
                  <MaterialLikeInput
                    required
                    label='Booking Number'
                    value={form.getFieldValue('booking_number')}
                  />
                </Form.Item>
              </Col>

              <Col span={24} className="mb-1">
                <h1 className="text-[#6A7282] uppercase text-sm">Schedule</h1>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="etd_date"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeDatePicker
                    label="ETD Date"
                    required
                    value={form.getFieldValue("etd_date")}
                    onChange={(date) => form.setFieldValue("etd_date", date)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="erd_date"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeDatePicker
                    label="ERD Date"
                    required
                    value={form.getFieldValue("erd_date")}
                    onChange={(date) => form.setFieldValue("erd_date", date)}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="cargo_cut_off_date"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeDatePicker
                    label="Cargo Cut Off Date"
                    required
                    value={form.getFieldValue("cargo_cut_off_date")}
                    onChange={(date) => form.setFieldValue("cargo_cut_off_date", date)}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="title_cut_off_date"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeDatePicker
                    label="Title Cut Off Date"
                    required
                    value={form.getFieldValue("title_cut_off_date")}
                    onChange={(date) => form.setFieldValue("title_cut_off_date", date)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="auto_cut_off_date"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeDatePicker
                    label="Auto Cut Off Date"
                    required
                    value={form.getFieldValue("auto_cut_off_date")}
                    onChange={(date) => form.setFieldValue("auto_cut_off_date", date)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="doc_cut_off_date"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeDatePicker
                    label="Doc Cut Off Date"
                    required
                    value={form.getFieldValue("doc_cut_off_date")}
                    onChange={(date) => form.setFieldValue("doc_cut_off_date", date)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="eta_date"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeDatePicker
                    label="ETA Date"
                    required
                    value={form.getFieldValue("eta_date")}
                    onChange={(date) => form.setFieldValue("eta_date", date)}
                  />
                </Form.Item>
              </Col>

              <Col span={24} className="mb-1" >
                <h1 className="text-[#6A7282] uppercase text-sm">Route and container</h1>
              </Col>


              <Col span={8}>
                <Form.Item
                  name="shipping_line"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeSelect
                    label="Shipping Line"
                    required
                    value={form.getFieldValue("shipping_line")}
                    onChange={(value) => form.setFieldValue('shipping_line', value)}
                    options={shippingLine.map((ship) => ({
                      id: ship.id,
                      value: ship.id,
                      label: ship.name,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="location"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeInput
                    required
                    label='Location'
                    value={form.getFieldValue('location')}
                    onChange={(e) => {
                      // Get the location input value
                      const locationValue = e.target.value;
                      // Append "-93000" to the location value to form the load number
                      const loadNumber = locationValue
                        ? `${locationValue}-${container_number}`
                        : "";
                      // Update the Load Number field dynamically
                      form.setFieldsValue({ load_number: loadNumber });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="destination"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeSelect
                    label="Destination"
                    required
                    value={form.getFieldValue("destination")}
                    onChange={(value) => form.setFieldValue('destination', value)}
                    options={destinations.map((destination) => ({
                      id: destination.id,
                      value: destination.name,
                      label: destination.name,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="container_size"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeSelect
                    label="Container Size"
                    required
                    value={form.getFieldValue("container_size")}
                    onChange={(value) => form.setFieldValue('container_size', value)}
                    options={containerSize.map((size) => ({
                      id: size.id,
                      value: size.name,
                      label: size.name,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="load_number">
                  <MaterialLikeInput
                    label='Load Number'
                    value={form.getFieldValue("load_number")}
                    readOnly={true}
                  />
                  {/* <Input placeholder="" readOnly style={{ height: "50px" }} /> */}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="is_dg"
                  rules={[
                    {
                      required: true,
                      message: "Please input!",
                    },
                  ]}
                >
                  <MaterialLikeSelect
                    label="Booking Type"
                    required
                    value={form.getFieldValue("is_dg")}
                    onChange={(value) => form.setFieldValue('is_dg', value)}
                    options={[
                      { id: 0, value: 0, label: "Non-DG" },
                      { id: 1, value: 1, label: "DG" },
                    ]}
                    dropdownMatchSelectWidth={true} // 👈 Add this
                  />
                </Form.Item>
              </Col>

              <Col span={24} className="mb-1">
                <h1 className="text-[#6A7282] uppercase text-sm">Documents</h1>
                <p>Upload Booking Confirmation Sheet</p>
              </Col>

              <Col span={8}>
                <Form.Item
                  // label="Upload Booking Confirmation Sheet"
                  className="w-full p-0 m-0"
                  style={{ width: "100%" }}
                >
                  <Upload
                    id="uploadInput"
                    action="/upload"
                    listType="picture-card"
                    beforeUpload={(file) => {
                      setUploadedFile(file); // store the selected file in state
                      return false; // prevent automatic upload
                    }}
                  // className="custom-upload-card"
                  >
                    <div className="flex flex-col gap-3">
                      <span>{<UploadOutlined style={{ fontSize: 24, color: "#6A7282" }} />}</span>
                      <span >upload</span>
                    </div>
                  </Upload>
                </Form.Item>
              </Col>
              <Col span={24} className="mb-5">
                <p className="text-[#99A1AF] text-xs">Attach the shipping line booking confirmation for this loading plan.</p>
              </Col>

              <Col span={24} className="mb-1">
                <h1 className="text-[#6A7282] uppercase text-sm">Note</h1>
              </Col>

              <Col span={24}>
                <Form.Item name="client_loading_note">
                  <MaterialLikeTextArea
                    label="Special Note"
                    rows={6}
                    placeholder="Add any special handling instructions or internal notes here"
                    value={form.getFieldValue("client_loading_note")}
                    onChange={(e) => form.setFieldValue("client_loading_note", e.target.value)}
                  />
                </Form.Item>
              </Col>

              <Col span={24} className="mb-1">
                <h1 className="text-[#6A7282] uppercase text-sm flex items-center gap-2">
                  Add Vehicle
                  <span className="text-sm text-red-500">
                    ({tableData.length}/5)
                  </span>
                </h1>
              </Col>

              {/* <Form.Item
                  // label="Add New Car"
                  name="new_car">
                  <AutoComplete
                    style={{ height: "45px" }}
                    options={newCarOptions}
                    onSearch={(query) => fetchCarOptions(query, setNewCarOptions)}
                    onSelect={(value, option) => handleSelect(value, option)}
                    placeholder="Search and select a car"

                  />
                </Form.Item> */}
              <Col span={24}>
                <Form.Item name="new_car">
                  <VehicleDropDown CarOptions={filteredOptions} fetchCarOptions={fetchCarOptions} setCarOptions={setNewCarOptions} handleSelect={handleSelect} loading={carLoading} />

                  {/* <VehicleDropDown CarOptions={newCarOptions} fetchCarOptions={fetchCarOptions} setCarOptions={setNewCarOptions} handleSelect={handleSelect} loading={vehicleLoader} /> */}
                </Form.Item>
              </Col>
            </Row>

            <Table
              columns={columns}
              pagination={false}
              scroll={{ x: "max-content" }}
              dataSource={tableData}
              style={{ marginTop: "5px" }}
            />
            <Row
              gutter={12}
              style={{
                marginTop: 40,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {/* <Col span={6}>
                <Button
                  type="primary"
                  // htmlType="submit"
                  style={{ width: "100%" }}
                  onClick={() => handleSubmit("save")}
                  loading={loading}
                >
                  SAVE
                </Button>
              </Col> */}
              <Col span={6}>
                <Button
                  type="primary"
                  // htmlType="submit"
                  style={{ width: "100%" }}
                  onClick={() => handleSubmit("Send to Ctrade")}
                  loading={loading}
                >
                  SEND TO CTRADE
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <style>
        {`
  .custom-upload-card .ant-upload-select,
  .custom-upload-card .ant-upload-list-item {
    width: 120px !important;
    height: 160px !important;
  }
  .custom-upload-card .ant-upload {
    width: 150px !important;
    height: 160px !important;
  }
`}
      </style>
    </>

  );
}
export default CreateLoadPlans;
